import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  addFinancialStatementNode,
  editFinancialStatementNode,
} from '../../../../../../../lib/api';
import {
  deleteFinancialStatementNode,
  getFinancialStatementsTreeTypes,
  getNodeClasses,
  getNodeClassifiedCategories,
} from '../../../../../../../lib/api/finnacialStatements';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import { LightTooltip } from '../../../../../../../lib/designSystem/WhiteTooltip';
import { handleError } from '../../../../../../../lib/functions/errors';
import {
  showAlert,
  validateParams,
} from '../../../../../../../lib/functions/utils';

interface TreeNode {
  id: string;
  key: string;
  children?: TreeNode[];
  accountAuxiliaryCode?: string | null;
  accountCode?: string | null;
  accountCodeType?: string | null;
  businessIdentifierRequired?: boolean;
  classId?: string | null;
  classifiedCategoryId?: string | null;
  costCenterRequired?: boolean;
  documentKeyRequired?: boolean;
  father?: number;
  financialStatementsTreeTypeId?: number;
  internalKey?: string | null;
}

interface EditNodeDialogProps {
  open: boolean;
  node: TreeNode | null;
  onClose: () => void;
  onSave: (nodeId: string, updatedNode: TreeNode) => void;
  onDelete?: (nodeId: string) => void; // Add delete callback
  allNodes?: TreeNode[]; // For father selection
  isNewNode?: boolean; // To distinguish between create and edit
  onSuccess?: (message: string) => void;
  onError?: (error: any) => void;
  setLoading?: (loading: boolean) => void;
  fetchSetupData?: () => Promise<void>;
}

const EditNodeDialog: React.FC<EditNodeDialogProps> = ({
  open,
  node,
  onClose,
  onSave,
  onDelete,
  allNodes = [],
  isNewNode = false,
  onSuccess,
  onError,
  setLoading,
  fetchSetupData,
}) => {
  const dispatch = useDispatch();
  const [editData, setEditData] = useState<TreeNode | null>(null);
  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [classifiedCategories, setClassifiedCategories] = useState<any>([]);
  const [classes, setClasses] = useState<any>([]);
  const [treeTypes, setTreeTypes] = useState<any>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (node) {
      setEditData(node);
    } else if (isNewNode) {
      setEditData({
        id: '',
        key: '',
        father: 0,
        financialStatementsTreeTypeId: undefined,
        accountCode: null,
        accountCodeType: null,
        accountAuxiliaryCode: null,
        internalKey: null,
        costCenterRequired: false,
        documentKeyRequired: false,
        businessIdentifierRequired: false,
        classifiedCategoryId: null,
        classId: null,
      });
    }

    setSaveError(null);

    const setUp = async () => {
      const responseClassifiedCategories =
        await getNodeClassifiedCategories(dispatch);
      const responseClasses = await getNodeClasses(dispatch);
      setClassifiedCategories(responseClassifiedCategories);
      setClasses(responseClasses);
      const responseTreeTypes = await getFinancialStatementsTreeTypes(dispatch);
      setTreeTypes(responseTreeTypes);
    };
    setUp();
  }, [node, isNewNode, open]);

  const validateExisting = () => {
    if (!isNewNode || !editData) return true;
    return !allNodes.find((x: any) => x.key === editData.key);
  };

  const handleSave = async () => {
    if (!editData) return;

    setSaveError(null);

    const requiredFields = isNewNode
      ? ['financialStatementsTreeTypeId', 'father', 'key']
      : ['key'];

    if (!validateParams(editData, requiredFields, alert)) return;

    if (isNewNode && !validateExisting()) {
      showAlert(dispatch, 'error', 'Cuenta ya existe');
      return;
    }

    if (setLoading) setLoading(true);

    try {
      if (isNewNode) {
        await addFinancialStatementNode(dispatch, editData);
      } else {
        await editFinancialStatementNode(dispatch, editData);
      }

      if (fetchSetupData) {
        await fetchSetupData();
      }

      const message = `Se ha ${isNewNode ? 'creado' : 'actualizado'} la cuenta exitosamente.`;
      showAlert(dispatch, 'success', message);

      if (onSuccess) {
        onSuccess(message);
      }

      if (editData.id) {
        onSave(editData.id, editData);
      }

      onClose();
    } catch (error: any) {
      console.log('Error in handleSave:', error);

      let errorMessage = 'Ha ocurrido un error al procesar la solicitud';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setSaveError(errorMessage);

      showAlert(dispatch, 'error', errorMessage);

      if (onError) {
        onError(error);
      }
      handleError(error);
    }

    if (setLoading) setLoading(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editData?.id) return;

    setShowDeleteConfirm(false);
    if (setLoading) setLoading(true);

    try {
      await deleteFinancialStatementNode(dispatch, editData.id);

      const message = 'Cuenta eliminada exitosamente.';
      showAlert(dispatch, 'success', message);

      if (onSuccess) {
        onSuccess(message);
      }

      if (onDelete) {
        onDelete(editData.id);
      }

      onClose();
    } catch (error: any) {
      console.log(error);
      showAlert(
        dispatch,
        'error',
        'Ha ocurrido un error al eliminar la cuenta',
      );

      if (onError) {
        onError(error);
      }
      handleError(error);
    }

    if (setLoading) setLoading(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleCancel = () => {
    setFormErrorMessage('');
    onClose();
  };

  if (!editData) {
    return null;
  }

  const hasInternalKey = !isNewNode && Boolean(editData.internalKey);

  const hasChildren =
    !isNewNode && editData.children && editData.children.length > 0;

  const cannotDelete = hasInternalKey || hasChildren;

  const getAvailableFathers = (
    currentNode: TreeNode,
    allNodesList: TreeNode[],
  ): TreeNode[] => {
    const excludeIds = new Set<string>();

    const addToExclude = (node: TreeNode) => {
      excludeIds.add(node.id);
      if (node.children) {
        node.children.forEach(addToExclude);
      }
    };
    addToExclude(currentNode);

    return allNodesList.filter((n) => !excludeIds.has(n.id));
  };

  const availableFathers = isNewNode
    ? allNodes
    : getAvailableFathers(editData, allNodes);

  return (
    <>
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {isNewNode ? 'Agregar cuenta' : `Editar Nodo: ${node?.key}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de balance</InputLabel>
                  <Select
                    value={editData.financialStatementsTreeTypeId || ''}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        financialStatementsTreeTypeId: e.target.value as number,
                      })
                    }
                    label="Tipo de balance"
                  >
                    {treeTypes.map((x: any) => (
                      <MenuItem key={x.id} value={x.id}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            }
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={[
                  {
                    id: '0',
                    key: 'Sin Padre (Raíz)',
                    label: 'Sin Padre (Raíz)',
                  },
                  ...availableFathers,
                ]}
                getOptionLabel={(option) => option.key || ''}
                value={
                  availableFathers.find(
                    (f) => f.id === editData.father?.toString(),
                  ) ||
                  (editData.father === 0
                    ? {
                        id: '0',
                        key: 'Sin Padre (Raíz)',
                        label: 'Sin Padre (Raíz)',
                      }
                    : null)
                }
                onChange={(event, newValue) => {
                  setEditData({
                    ...editData,
                    father: newValue ? parseInt(newValue.id) : 0,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cuenta Padre"
                    disabled={
                      isNewNode && !editData.financialStatementsTreeTypeId
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No hay nodos disponibles"
                clearOnBlur
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre de la cuenta"
                value={editData.key || ''}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    key: e.target.value,
                  })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código de Cuenta"
                value={editData.accountCode || ''}
                onChange={(e) =>
                  setEditData({ ...editData, accountCode: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Código</InputLabel>
                <Select
                  value={editData.accountCodeType || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      accountCodeType: e.target.value,
                    })
                  }
                  label="Tipo de Código"
                >
                  <MenuItem value="asset">Activo</MenuItem>
                  <MenuItem value="liability">Pasivo</MenuItem>
                  <MenuItem value="equity">Patrimonio</MenuItem>
                  <MenuItem value="revenue">Ingreso</MenuItem>
                  <MenuItem value="expense">Gasto</MenuItem>
                  {
                    //TODO: Add clearing account type translation
                  }
                  <MenuItem value="clearing">Liquidación</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría Balance Clasificado</InputLabel>
                <Select
                  fullWidth
                  value={editData.classifiedCategoryId}
                  label="Categoría de asiento"
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      classifiedCategoryId: e.target.value,
                    })
                  }
                >
                  {classifiedCategories.map((category: any) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Clase</InputLabel>
                <Select
                  fullWidth
                  value={editData.classId}
                  label="Clase"
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      classId: e.target.value,
                    })
                  }
                >
                  {classes.map((classItem: any) => (
                    <MenuItem key={classItem.id} value={classItem.id}>
                      {classItem.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Campos Obligatorios al generar asientos
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editData.costCenterRequired || false}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        costCenterRequired: e.target.checked,
                      })
                    }
                  />
                }
                label="Centro de Costo"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editData.documentKeyRequired || false}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        documentKeyRequired: e.target.checked,
                      })
                    }
                  />
                }
                label="Tipo de Documento"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editData.businessIdentifierRequired || false}
                    onChange={(e) =>
                      setEditData({
                        ...editData,
                        businessIdentifierRequired: e.target.checked,
                      })
                    }
                  />
                }
                label="Auxiliar (RUT)"
              />
            </Grid>
          </Grid>
          {formErrorMessage && (
            <Typography variant="body1" color="error" sx={{ mt: 2 }}>
              {formErrorMessage}
            </Typography>
          )}
          {saveError && (
            <Typography
              variant="body1"
              color="error"
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'error.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'error.main',
              }}
            >
              <strong>Error:</strong> {saveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {!isNewNode && (
            <LightTooltip
              title={
                <Typography padding={1}>
                  {hasInternalKey
                    ? 'Esta cuenta no se puede eliminar ya que es generada por el sistema para asientos transaccionales'
                    : hasChildren
                      ? 'Esta cuenta no se puede eliminar ya que tiene cuentas hijas. Primero debe eliminar todas las cuentas hijas.'
                      : ''}
                </Typography>
              }
            >
              <span>
                <CustomButton
                  onClick={handleDeleteClick}
                  variant="outlined"
                  color="error"
                  disabled={cannotDelete}
                >
                  Eliminar
                </CustomButton>
              </span>
            </LightTooltip>
          )}
          <CustomButton onClick={handleCancel}>Cancelar</CustomButton>
          <CustomButton onClick={handleSave} variant="contained">
            {isNewNode ? 'Crear' : 'Guardar'}
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirm}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de que desea eliminar la cuenta "{editData?.key}"? Esta
            acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={handleDeleteCancel}>Cancelar</CustomButton>
          <CustomButton
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditNodeDialog;
